/** IndexedDB backend for `storage.ts`: one object store per collection, keyed by the records' `id`. */
import { COLLECTIONS, META, RECORDINGS, type Backend, type StoredRecord } from "./storage";

const DB_NAME = "workout-helper";
/**
 * IndexedDB's own version only tracks which object stores exist; raise it when a collection is added. The shape of
 * the records is versioned separately (the migrations in storage.ts).
 */
const IDB_VERSION = 2;

function openDb(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, IDB_VERSION);
    req.onupgradeneeded = () => {
      for (const name of [META, ...COLLECTIONS, RECORDINGS]) {
        if (!req.result.objectStoreNames.contains(name)) req.result.createObjectStore(name, { keyPath: "id" });
      }
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
    req.onblocked = () => reject(new Error("The database is open elsewhere with an older version. Close it and reload."));
  });
}

export function idbBackend(): Backend {
  let dbPromise: Promise<IDBDatabase> | undefined;
  const run = <T,>(collection: string, mode: IDBTransactionMode, fn: (s: IDBObjectStore) => IDBRequest) =>
    (dbPromise ??= openDb()).then((db) => new Promise<T>((resolve, reject) => {
      const tx = db.transaction(collection, mode);
      const req = fn(tx.objectStore(collection));
      tx.oncomplete = () => resolve(req.result as T);
      tx.onerror = () => reject(tx.error);
      tx.onabort = () => reject(tx.error);
    }));
  return {
    getAll: <T extends StoredRecord>(c: string) => run<T[]>(c, "readonly", (s) => s.getAll()),
    get: <T extends StoredRecord>(c: string, id: string) => run<T | undefined>(c, "readonly", (s) => s.get(id)),
    put: (c, record) => run<void>(c, "readwrite", (s) => s.put(record)),
    delete: (c, id) => run<void>(c, "readwrite", (s) => s.delete(id)),
  };
}
